// ============================================
// Save Converter — Unity SaveData → Web SaveData
// Handles: parallel arrays → objects, SMS flattening,
// missing fields, version migration
// ============================================

/**
 * Detect if a save object is in Unity format (parallel arrays)
 * vs Web format (objects).
 */
export function isUnityFormat(data) {
    return Array.isArray(data.baseKeys) && Array.isArray(data.baseValues);
}

/**
 * Convert Unity SaveData (C# JsonUtility) to Web SaveData format.
 * Safe to call on already-converted data (returns as-is).
 */
export function convertUnitySave(unity) {
    if (!isUnityFormat(unity)) return unity; // Already web format

    const web = {};

    // ── Metadata ──
    web.saveVersion = unity.saveVersion || 4;
    web.gameVersion = unity.gameVersion || '0.2.0';
    web.timestamp = unity.timestamp || '';
    web.day = unity.dayNumber || 0;
    web.playerName = unity.playerName || '';
    web.slotIndex = unity.slotIndex ?? 0;

    // ── Ink State ──
    // Both formats use the same inkjs JSON, field name differs
    web.inkState = unity.inkStateJson || unity.inkState || '';

    // ── Stats: parallel arrays → object ──
    web.baseStats = _arraysToObject(unity.baseKeys, unity.baseTypes, unity.baseValues);
    web.buffedStats = _arraysToObject(unity.buffedKeys, unity.buffedTypes, unity.buffedValues);

    // Extract timeSlot from stats if not top-level
    web.timeSlot = web.baseStats.TimeSlot || 0;
    web.day = web.baseStats.Day || web.day;

    // ── Flags: extract boolean stats into flags object ──
    web.flags = {};
    const boolKeys = Object.keys(web.baseStats).filter(k => typeof web.baseStats[k] === 'boolean');
    for (const k of boolKeys) {
        web.flags[k] = web.baseStats[k];
    }

    // ── Inventory ──
    web.ownedItemTags = unity.ownedItemTags || [];
    web.equippedSlots = unity.equippedSlots || [];
    web.equippedItemTags = unity.equippedItemTags || [];

    // ── SMS: 7 parallel arrays → array of objects ──
    web.smsMessages = [];
    const smsCount = (unity.smsConversationIds || []).length;
    for (let i = 0; i < smsCount; i++) {
        web.smsMessages.push({
            npcId: unity.smsConversationIds[i] || '',
            text: (unity.smsTexts || [])[i] || '',
            imageName: (unity.smsImageNames || [])[i] || null,
            isFromPlayer: (unity.smsIsFromPlayer || [])[i] || false,
            dayReceived: (unity.smsDays || [])[i] || 0,
            timeSlotReceived: (unity.smsTimeSlots || [])[i] || 0,
            isRead: (unity.smsReadFlags || [])[i] || false,
        });
    }

    // ── Fields that don't exist in Unity saves (initialize empty) ──
    web.statistics = {};
    web.unlockedAchievements = [];
    web.characterData = null;
    web.perkSelections = [];
    web.galleryUnlocked = [];

    return web;
}

/**
 * Convert parallel key/type/value arrays to a plain object.
 * Handles Unity's type encoding: "int32" → int, "boolean" → bool, "single" → float, "string" → string
 */
function _arraysToObject(keys, types, values) {
    const obj = {};
    if (!keys || !values) return obj;

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const type = (types || [])[i] || 'string';
        const raw = values[i];

        switch (type.toLowerCase()) {
            case 'int32':
            case 'int':
                obj[key] = parseInt(raw, 10) || 0;
                break;
            case 'single':
            case 'float':
            case 'double':
                obj[key] = parseFloat(raw) || 0;
                break;
            case 'boolean':
            case 'bool':
                obj[key] = raw === 'true' || raw === 'True' || raw === true || raw === 1;
                break;
            default:
                obj[key] = raw;
        }
    }
    return obj;
}

// Legacy auto-import (tryAutoImportUnitySaves) was removed in v0.2.8; it used the
// now-removed desktop registry bridge that a few antivirus heuristics flagged.
// convertUnitySave() above stays in use for the file-based Unity import path.
